// src/pages/RegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import FolioInput from '../components/FolioInput';
import DigitalPassCard from '../components/DigitalPassCard';
import LegalModal from '../components/LegalModal';
import { registerParticipant } from '../services/ticketApi';
import { trackMetric, detectAndStoreTrafficSource } from '../services/telemetryService';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();

  // Parámetros de URL
  const sourceParam = searchParams.get('x'); // 'b' = boleto físico
  const initialTicketParam = searchParams.get('t') || '';

  const isPhysicalTicket = sourceParam === 'b';
  const registrationSource = isPhysicalTicket ? 'b' : (sourceParam || 'direct');

  // Estado del formulario
  const [folio, setFolio] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [originCityCountry, setOriginCityCountry] = useState('');
  const [hasXolopass, setHasXolopass] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptsPromotions, setAcceptsPromotions] = useState(false);

  // Estado del modal legal
  const [legalModalOpen, setLegalModalOpen] = useState(false);

  // Estado de envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [mismatchError, setMismatchError] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [hasTrackedFormStart, setHasTrackedFormStart] = useState(false);

  // Telemetría al montar la página de registro
  useEffect(() => {
    const detected = detectAndStoreTrafficSource();
    if (detected === 'b' || detected === 'a') {
      trackMetric('qr_scan', { trafficSource: detected });
    }
    trackMetric('page_view', { page: 'register', trafficSource: detected });
  }, []);

  // Precargar folio desde query param ?t= al montar (sin validación debounce)
  useEffect(() => {
    if (initialTicketParam) {
      const sanitized = initialTicketParam.replace(/\D/g, '').slice(0, 4);
      if (sanitized) {
        setFolio(sanitized);
      }
    }
  }, [initialTicketParam]);

  // Handlers de inputs
  const triggerFormStart = () => {
    if (!hasTrackedFormStart) {
      setHasTrackedFormStart(true);
      trackMetric('form_start', { trafficSource: registrationSource });
    }
  };

  const handleFolioChange = (val) => {
    triggerFormStart();
    setFolio(val);
    setMismatchError(null);
    setGeneralError(null);
  };

  const handlePhoneChange = (e) => {
    triggerFormStart();
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(raw);
  };

  // Envío del registro
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMismatchError(null);
    setGeneralError(null);

    // Validaciones locales
    if (!folio || folio.length !== 4) {
      setGeneralError('Por favor ingresa el folio de tu boleto (4 dígitos).');
      return;
    }

    if (!fullName.trim()) {
      setGeneralError('Por favor escribe tu nombre completo.');
      return;
    }

    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setGeneralError('Por favor escribe tu teléfono / WhatsApp a 10 dígitos.');
      return;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setGeneralError('Por favor escribe un correo electrónico válido.');
      return;
    }

    if (!referralSource.trim()) {
      setGeneralError('Por favor indica qué marca o medio te regaló tu acceso.');
      return;
    }

    if (!originCityCountry.trim()) {
      setGeneralError('Por favor indica de qué ciudad o país nos visitas.');
      return;
    }

    if (!acceptedTerms) {
      setGeneralError('Debes aceptar el Aviso de privacidad.');
      return;
    }

    setIsSubmitting(true);

    const response = await registerParticipant({
      folio,
      fullName,
      email,
      phone: cleanPhone,
      source: registrationSource,
      referralSource,
      originCityCountry,
      hasXolopass,
      acceptedPrivacyPolicy: acceptedTerms,
      privacyPolicyVersion: '2026-v1.0',
      acceptsCommercialCommunications: acceptsPromotions,
      acceptsPromotions
    });

    setIsSubmitting(false);

    if (response.success) {
      trackMetric('register_success', {
        ticketFolio: folio,
        trafficSource: registrationSource
      });
      setSubmissionResult(response);
    } else if (response.errorType === 'MISMATCH_DATA') {
      setMismatchError(response.message);
    } else {
      setGeneralError(response.message || 'Ocurrió un error al procesar el registro. Intenta de nuevo.');
    }
  };

  // Disparar confeti rojinegro y dorado al registrarse exitosamente
  useEffect(() => {
    if (submissionResult && submissionResult.success) {
      const xoloColors = ['#dc2626', '#ffffff', '#000000', '#ef4444'];

      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: xoloColors,
        disableForReducedMotion: true
      });

      const timer = setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 60,
          origin: { x: 0.05, y: 0.7 },
          colors: xoloColors
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 60,
          origin: { x: 0.95, y: 0.7 },
          colors: xoloColors
        });
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [submissionResult]);

  const handleReset = () => {
    setSubmissionResult(null);
    setMismatchError(null);
    setGeneralError(null);
    setFolio('');
    setFullName('');
    setEmail('');
    setPhone('');
    setReferralSource('');
    setOriginCityCountry('');
    setHasXolopass(false);
    setAcceptedTerms(false);
    setAcceptsPromotions(false);
  };

  return (
    <div
      className="register-page-wrapper py-4 py-md-5 position-relative overflow-hidden"
      style={{ minHeight: '88vh', background: 'transparent' }}
    >
      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* BOTÓN DE REGRESO ALINEADO CON EL ANCHO DEL FORMULARIO */}
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-10 col-lg-7 col-xl-6">
            <Link
              to="/"
              className="btn btn-xolo-outline rounded-circle d-inline-flex align-items-center justify-content-center shadow"
              style={{
                width: '42px',
                height: '42px',
                padding: 0
              }}
              aria-label="Regresar a inicio"
              title="Regresar a la página principal"
            >
              <i className="bi bi-arrow-left fs-5"></i>
            </Link>
          </div>
        </div>

        {/* ENCABEZADO OFICIAL DE REGISTRO */}
        <div className="text-center mb-4">
          {/* LOGO XOLO FEST */}
          <div className="d-flex justify-content-center mb-3">
            <img
              src="/img/brand/logo-xolofest-horizontal.png"
              alt="Xolo Fest 2026"
              style={{
                maxHeight: '75px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 18px rgba(0, 0, 0, 0.6))'
              }}
            />
          </div>

          <h1 className="h2 text-white fw-black mb-2" style={{ textShadow: '0 4px 18px rgba(0, 0, 0, 0.95)' }}>
            Registra tu boleto y participa en la <span className="text-white" style={{ textShadow: '0 4px 20px rgba(0, 0, 0, 0.95), 0 0 20px rgba(220, 38, 38, 0.6)' }}>Tómbola Perrona</span>
          </h1>
          <p className="text-light opacity-90 mx-auto" style={{ maxWidth: '640px' }}>
            Completa tu registro en un solo paso. Ingresa el folio de tu boleto, tus datos y obtén tu pase digital oficial.
          </p>
        </div>

        {/* CONTENEDOR PRINCIPAL */}
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-7 col-xl-6">
            <div
              className="ticket-form-card shadow-2xl p-4 p-md-5 rounded-4"
              style={{
                backgroundColor: 'rgba(17, 17, 22, 0.95)',
                border: '1px solid rgba(220, 38, 38, 0.35)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {!submissionResult ? (
                /* FORMULARIO DE REGISTRO LIMPIO */
                <>
                  {/* ALERTA DE DISCREPANCIA DE DATOS (MISMATCH_DATA) */}
                  {mismatchError && (
                    <div className="alert alert-danger border-danger border-opacity-50 bg-danger bg-opacity-10 text-light mb-4" role="alert">
                      <div className="d-flex align-items-start gap-2">
                        <i className="bi bi-shield-x text-danger fs-4 mt-1"></i>
                        <div>
                          <strong className="text-danger d-block fs-6">Boleto registrado</strong>
                          <p className="small mb-2 text-light opacity-90">{mismatchError}</p>
                          <div className="small text-light opacity-80 border-top border-danger border-opacity-25 pt-2">
                            💡 Si este boleto te pertenece, asegúrate de ingresar el mismo Teléfono y Correo Electrónico que registraste originalmente, o acude al módulo de atención en el Estacionamiento del Estadio Caliente.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ALERTA GENERAL DE ERROR */}
                  {generalError && (
                    <div className="alert alert-danger border-danger border-opacity-50 bg-danger bg-opacity-10 text-light mb-4" role="alert">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-exclamation-circle text-danger fs-5"></i>
                        <span className="small">{generalError}</span>
                      </div>
                    </div>
                  )}

                  {/* FORMULARIO */}
                  <form onSubmit={handleSubmit} noValidate>
                    {/* CAMPO 1: FOLIO DE BOLETO CON IDENTIFICADOR SÚTIL DE CATEGORÍA */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold">
                        Ingresa el folio de tu boleto (4 dígitos)
                      </label>
                      <FolioInput
                        value={folio}
                        onChange={handleFolioChange}
                        disabled={isSubmitting}
                      />
                      {folio && folio.length === 4 && (() => {
                        const num = parseInt(folio, 10);
                        if (num >= 1 && num <= 6700) {
                          return (
                            <div className="mt-2 d-flex align-items-center gap-2 small">
                              <span className="badge bg-secondary bg-opacity-50 text-white fw-medium px-2 py-1">
                                <i className="bi bi-ticket-perforated me-1 text-danger"></i>
                                Boleto General
                              </span>
                            </div>
                          );
                        }
                        if (num >= 6701 && num <= 7000) {
                          return (
                            <div className="mt-2 d-flex align-items-center gap-2 small">
                              <span className="badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50 fw-semibold px-2 py-1">
                                <i className="bi bi-star-fill me-1"></i>
                                Boleto VIP
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* CAMPO 2: NOMBRE COMPLETO */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold">
                        Escribe tu nombre completo
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                          <i className="bi bi-person-fill"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-dark"
                          placeholder="Nombre y apellidos"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    {/* CAMPO 3: TELÉFONO / WHATSAPP */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold d-flex justify-content-between align-items-center">
                        <span>Escribe tu teléfono / WhatsApp (10 dígitos)</span>
                        <span className="text-light opacity-75 small font-monospace">{phone.length}/10</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                          <i className="bi bi-whatsapp"></i>
                        </span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="6641234567"
                          className="form-control form-control-dark font-monospace"
                          value={phone}
                          onChange={handlePhoneChange}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    {/* CAMPO 4: CORREO ELECTRÓNICO */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold">
                        Escribe tu correo electrónico
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                          <i className="bi bi-envelope-fill"></i>
                        </span>
                        <input
                          type="email"
                          className="form-control form-control-dark"
                          placeholder="aficionado@ejemplo.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    {/* CAMPO 5: ¿QUÉ MARCA O MEDIO TE REGALÓ TU ACCESO? (OBLIGATORIO) */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold d-block">
                        ¿Qué marca o medio te regaló tu acceso?
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                          <i className="bi bi-gift-fill"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-dark"
                          placeholder="Ej: Caliente, Patrocinador, Radio, etc."
                          value={referralSource}
                          onChange={(e) => setReferralSource(e.target.value)}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    {/* CAMPO 6: ¿DE QUÉ CIUDAD O PAÍS NOS VISITAS? (OBLIGATORIO) */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold d-block">
                        ¿De qué ciudad o país nos visitas?
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                          <i className="bi bi-geo-alt-fill"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-dark"
                          placeholder="Ej: Tijuana, San Diego, Mexicali, etc."
                          value={originCityCountry}
                          onChange={(e) => setOriginCityCountry(e.target.value)}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    {/* CAMPO 7: ¿CUENTAS CON XOLOPASS? (SELECTOR SÍ / NO EN ROJO/NEGRO) */}
                    <div className="mb-4">
                      <label className="form-label text-light small fw-bold d-block mb-2">
                        ¿Cuentas con Xolopass?
                      </label>
                      <div className="btn-group w-100" role="group" aria-label="¿Cuentas con Xolopass?">
                        <button
                          type="button"
                          className={`btn py-2 fw-bold text-uppercase ${
                            hasXolopass === true
                              ? 'btn-danger shadow'
                              : 'btn-outline-secondary text-light bg-black bg-opacity-50'
                          }`}
                          style={{
                            borderColor: hasXolopass === true ? '#dc2626' : 'rgba(255, 255, 255, 0.2)'
                          }}
                          onClick={() => setHasXolopass(true)}
                          disabled={isSubmitting}
                        >
                          <i className="bi bi-check-circle-fill me-1"></i> Sí
                        </button>
                        <button
                          type="button"
                          className={`btn py-2 fw-bold text-uppercase ${
                            hasXolopass === false
                              ? 'btn-dark border-secondary text-white shadow'
                              : 'btn-outline-secondary text-light bg-black bg-opacity-50'
                          }`}
                          style={{
                            borderColor: hasXolopass === false ? '#71717a' : 'rgba(255, 255, 255, 0.2)',
                            backgroundColor: hasXolopass === false ? '#27272a' : undefined
                          }}
                          onClick={() => setHasXolopass(false)}
                          disabled={isSubmitting}
                        >
                          <i className="bi bi-x-circle me-1"></i> No
                        </button>
                      </div>
                    </div>

                    {/* CAMPO 8: TÉRMINOS, CONDICIONES Y AVISO DE PRIVACIDAD (UNIFICADO) */}
                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input bg-dark border-secondary"
                        id="termsCheck"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        disabled={isSubmitting}
                        required
                      />
                      <label className="form-check-label text-light opacity-90 small" htmlFor="termsCheck">
                        He leído y acepto el{' '}
                        <button
                          type="button"
                          className="btn btn-link text-white p-0 text-decoration-underline align-baseline small border-0 bg-transparent fw-semibold"
                          onClick={() => setLegalModalOpen(true)}
                        >
                          Aviso de privacidad
                        </button>
                      </label>
                    </div>

                    {/* CAMPO 9: PROMOCIONES Y NOTICIAS OFICIALES (OPCIONAL) */}
                    <div className="mb-4 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input bg-dark border-secondary"
                        id="promotionsCheck"
                        checked={acceptsPromotions}
                        onChange={(e) => setAcceptsPromotions(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <label className="form-check-label text-light opacity-90 small" htmlFor="promotionsCheck">
                        Deseo recibir noticias, promociones exclusivas y sorpresas del Club Tijuana Xoloitzcuintles de Caliente y sus patrocinadores oficiales.
                      </label>
                    </div>

                    {/* BOTÓN CTA PRINCIPAL */}
                    <button
                      type="submit"
                      className="btn btn-xolo-primary w-100 py-3 fw-bold fs-6 text-uppercase letter-spacing-md shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Generando tu Pase Digital...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-qr-code-scan me-2"></i>
                          Registrar boleto
                        </>
                      )}
                    </button>
                  </form>

                  {/* ENLACE SECUNDARIO A RECUPERACIÓN */}
                  <div className="text-center mt-4 pt-3 border-top border-secondary border-opacity-25">
                    <span className="text-light opacity-90 small">¿Ya registraste tu boleto antes? </span>
                    <Link to="/r/recuperar" className="text-danger text-decoration-none small fw-bold">
                      Consúltalo aquí
                    </Link>
                  </div>
                </>
              ) : (
                /* VISTA DEL PASE GENERADO (WALLETCARD) */
                <DigitalPassCard
                  passData={submissionResult.data || submissionResult.pass}
                  isRecovered={submissionResult.isRecovered}
                  onReset={handleReset}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL LEGAL INTERACTIVO */}
      <LegalModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
      />
    </div>
  );
}
